import type { PreflightResult } from '@ctcells/contract'
import { t, type MessageKey } from '@ctcells/i18n'
import { useState } from 'react'

import { Button } from '../atoms/Button.tsx'
import { Checkbox } from '../atoms/Checkbox.tsx'
import { Icon } from '../atoms/Icon.tsx'
import { Modal } from '../atoms/Modal.tsx'
import { gate, type ActionGate, type ScreenAccess } from '../screens/access.ts'
import {
  PreflightReport,
  WarningList,
  type PreflightReportProps,
} from '../screens/batches/PreflightReport.tsx'
import { ackCodes, type StartGate } from './startGate.ts'
import { FixablePreflightReport } from './StartPreflightActions.tsx'

// `.vissue`(실패 문구 한 줄)는 `resource.css` · `.pfblk__*`(점검 목록 꼴)는 `screens.css` 에 산다
import '../screens/resource.css'
import '../screens/screens.css'
import './stripStartDialog.css'

/**
 * 상시 작업 줄의 **시작 게이트 대화** — 경고 확약(`warn_ack`)이 사는 자리.
 *
 * 🔴 **[시작]은 되돌릴 수 있는 명령인데도 여기를 지난다.** 사유는 하나다 — 서버가
 *    **Pre-flight 경고를 확약하지 않은 시작을 거부**한다(`ctcells-core@6683b7a`,
 *    409 `BATCH_COMMAND_NOT_ALLOWED` · `context.reason = warn_ack_required`).
 *    확인 대화를 손이 한 번 더 가게 하려고 붙인 것이 아니라, **무엇을 알고 눌렀는지**가
 *    감사 원장에 남는 칸(`audit_log` `start_warn_ack`)의 입력이기 때문이다.
 *    ⚠ 최초 시작은 경고가 0건이면 바로 보낸다. 조치 후 재점검은 명시적 [시작]을 기다린다.
 *
 * 🔴 **차단 · 경고 확약 · 재점검 후 시작 확인이 한 컴포넌트다.**
 *    | `phase`   | 보이는 것                                   | 확정 버튼 |
 *    | --------- | ------------------------------------------- | --------- |
 *    | `blocked` | `PreflightReport` 그대로                     | **없다**  |
 *    | `warn`    | `PreflightReport`(있으면) + 확약 체크        | [시작]    |
 *    차단은 확약으로 우회되지 않는다(`blockers` 뒤에 확약 게이트가 선다) — 그래서 그 얼굴에는
 *    확정 버튼 자체를 두지 않는다. 비활성으로 두면 「확인하면 눌리는 줄」로 읽힌다.
 *
 * 🔴 **경고 얼굴도 보고서를 통째로 그린다 — 경고만 떼어 보이지 않는다.** 서버가 통과시킨
 *    회차에도 **미판정 항목**이 남아 있다(실측 2026-09-22 — 비전 판정 불능 · 시약 온도 미도달 ·
 *    소모품 미확인 4건이 `passed: true` 와 같이 왔다). 그것을 숨기면 사람이 **「못 쟀다」를
 *    모른 채** 확약한다. `PreflightReport` 안에 `WarningList` 가 이미 들어 있어 목록이 겹치지 않는다.
 *    ⚠ 서버가 정정해 준 목록(`reason` 있음)에는 보고서가 없다 — 그 결과는 이미 낡았다.
 *
 * 🔴 **목록을 그리는 것은 「새 작업」 화면과 «같은 컴포넌트»다**(`screens/batches/PreflightReport`).
 *    두 화면이 다른 말을 하면 사람이 무엇을 확약했는지 모르고, 갈리는 순간 서버가
 *    `warn_ack_mismatch` 로 거절한다. 여기에 목록을 다시 짓지 마라.
 *
 * 🔴 **체크는 목록이 갈리면 풀린다.** 컨테이너가 `startGateKey()` 를 React `key` 로 주어
 *    이 컴포넌트를 다시 세운다 — 한 목록에서 받은 확인이 다른 목록으로 넘어가면
 *    그것이 곧 「다른 것을 보고 확약한 것」이다.
 *
 * 🔴 **실패해도 닫히지 않는다.** 실패 문구가 그 자리에 뜨고 사람이 다시 누른다 —
 *    **자동 재시도는 하지 않는다**(서버가 정답을 줬다고 화면이 대신 확약할 수 없다).
 */

/** 서버가 정정해 준 갈래 → 그 자리에 뜨는 한 줄 */
const NOTICE: Record<string, MessageKey> = {
  warn_ack_required: 'strip.warnAckMissing',
  warn_ack_snapshot_missing: 'strip.warnAckMissing',
  warn_ack_mismatch: 'strip.warnAckChanged',
}

export interface StripStartDialogProps {
  presentation?: PreflightReportProps['presentation']
  gate: StartGate
  /** 대상 작업 id */
  batchId?: string | undefined
  /** [시작] 버튼과 **같은 판정**을 확정 버튼에도 건다 */
  access?: ScreenAccess
  /** 요청이 날아가 있는 동안. 같은 명령을 두 번 보내지 못하게 한다 */
  pending?: boolean | undefined
  /** 실패 문구. 컨테이너가 `errorMessage()`(에러 코드 사전)로 만들어 넘긴다 */
  errorMessage?: string | undefined
  onCancel: () => void
  /** 확약한 **코드 전부**를 싣고 시작한다 */
  onConfirm: (codes: string[]) => void
  /**
   * 차단 얼굴에서 줄마다 조치 단추 · [사전 점검 다시]를 단다(eric 2026-10-23 · `StartPreflightActions.tsx`).
   * 작업 줄이 켠다. 안 주면 종전 그대로(보고서만)다 — 다른 창구(개발용 「바로 돌리기」 등)는 그대로다
   */
  fixable?: boolean | undefined
  /**
   * 조치 칸에서 다시 돈 점검이 통과했다 — 컨테이너가 같은 창을 다음 얼굴(경고 확약 → [시작])로 바꾼다(`useStartGate.adopt`).
   * `batchId` 는 그 결과를 낸 작업(다시 만들었으면 새 작업)이다
   */
  onPassed?: ((result: PreflightResult, batchId: string) => void) | undefined
}

export function StripStartDialog({
  gate: startGate,
  batchId,
  access = {},
  pending,
  errorMessage,
  onCancel,
  onConfirm,
  fixable = false,
  onPassed,
  presentation = 'list',
}: StripStartDialogProps) {
  const [acked, setAcked] = useState(false)
  /*
   * 조치 창이 붙잡는 작업 — 창을 연 때의 작업이다. 작업 줄의 선택이 바뀌어도 조치 대상은 바꾸지 않는다.
   */
  const [fixBatchId] = useState(batchId)
  const blocked = startGate.phase === 'blocked'

  /**
   * 🔴 **[시작] 버튼과 같은 세 축이다**(락 · 상태 · 권한). 대화를 열 수 있었다고 확정까지
   *    열어 두지 않는다 — 대화가 떠 있는 동안 제어락을 뺏기거나 구독이 끊길 수 있다.
   *    `stateOk` 만 이 대화 몫이다: **사람이 확인 체크를 눌렀는가.**
   */
  const confirmGate: ActionGate = pending
    ? { disabled: true, disabledReason: t('reason.actionInFlight') }
    : gate(access, {
        perm: 'control',
        needsLock: true,
        stateOk: startGate.phase === 'confirm' || acked,
        stateReason: 'reason.warnAckRequired',
      })

  const notice = startGate.phase === 'warn' ? NOTICE[startGate.reason ?? ''] : undefined

  return (
    <Modal
      footer={
        <>
          <Button onClick={onCancel} size="pill">
            {/* 🔴 차단은 **닫는 것**이고 경고는 **그만두는 것**이다 — 낱말을 갈라 쓴다 */}
            {t(blocked ? 'action.close' : 'action.cancel')}
          </Button>
          {/* 🔴 차단에는 확정 버튼이 «없다». 비활성으로 두면 확인하면 눌리는 줄로 읽힌다 */}
          {blocked ? null : (
            <Button
              onClick={() => {
                onConfirm(ackCodes(startGate))
              }}
              size="pill"
              variant="primary"
              {...confirmGate}
            >
              {t('action.start')}
            </Button>
          )}
        </>
      }
      onClose={onCancel}
      size="lg"
      title={t('preflight.title')}
    >
      {/*
       * 🔴 **위험이 아니라 «주의»다.** 아래 `.vissue`(실패 문구)와 같은 면을 쓰면
       *    「보냈는데 안 됐다」로 읽히는데, 이 줄의 뜻은 **「다른 것을 보고 확약할 뻔했다」**다.
       *    색만으로 가르지 않으려고 글리프(△)가 글자와 같이 말한다.
       */}
      {notice === undefined ? null : (
        <p className="pfblk__head" role="status">
          <Icon name="alert" size="md" />
          {t(notice)}
        </p>
      )}

      {/* 🔴 서버가 준 결과 **그대로**. 「새 작업」 화면과 한 컴포넌트다 */}
      {startGate.phase === 'blocked' ? (
        fixable && fixBatchId !== undefined ? (
          <FixablePreflightReport
            access={access}
            batchId={fixBatchId}
            initial={startGate.result}
            presentation={presentation}
            hideTitle
            {...(onPassed === undefined ? {} : { onPassed })}
          />
        ) : (
          <PreflightReport
            result={startGate.result}
            presentation={presentation}
            hideTitle
            startedOnce
          />
        )
      ) : startGate.phase === 'confirm' ? (
        <PreflightReport
          result={startGate.result}
          presentation={presentation}
          hideTitle
          startedOnce
        />
      ) : (
        <>
          {startGate.result === undefined ? (
            /* 정정본 — 보고서가 없다. 서버가 준 «코드 집합»만 그린다 */
            <WarningList warnings={startGate.warnings} presentation={presentation} hideTitle />
          ) : (
            <PreflightReport
              result={startGate.result}
              presentation={presentation}
              hideTitle
              startedOnce
            />
          )}
          <div className="strip-start__ack">
            <Checkbox
              checked={acked}
              label={t('preflight.warnAck')}
              onChange={setAcked}
              {...(pending === true
                ? { disabled: true, disabledReason: t('reason.actionInFlight') }
                : {})}
            />
          </div>
        </>
      )}

      {errorMessage === undefined ? null : (
        <p className="vissue" role="alert">
          {errorMessage}
        </p>
      )}
    </Modal>
  )
}
